<template>
  <div>
    <Activation-Popup
      v-if="showActivationPopup"
      v-on:closed="showActivationPopup = false"
    />
    <div v-if="!loadingCompany" class="company-info-section">
      <div
        v-if="!loadingCompany && companyData"
        class="company-info animate-fadein"
      >
        <div v-if="companyData.name" class="company-logo-with-name">
          <div class="logo-wrap flex-c">
            <img
              :src="companyData.logoUrl"
              class="company-logo"
              v-if="companyData.logoUrl"
            />
            <div v-else>{{ companyData.name.slice(0, 1) }}</div>
          </div>
          <div class="name-wrap">
            <div class="company-name">
              {{ companyData.name }}
            </div>
            <p class="company-industry">{{ companyData.industry }}</p>
          </div>
        </div>
        <div
          class="decision-link"
          v-if="companyData && companyData.primaryDomain && companyData.website"
        >
          <Decision-Link viewBox="0 0 15 15" />
          <div class="label-with-value">
            <div class="company-label">Website</div>
            <a :href="companyData.website" target="_blank" class="txt-blue">{{
              companyData.primaryDomain
            }}</a>
          </div>
        </div>
        <div
          class="location d-flex float-l width-100"
          v-if="
            companyData.street ||
              companyData.city ||
              companyData.state ||
              companyData.country
          "
        >
          <Location viewBox="0 0 20 20" />
          <div class="label-with-value">
            <div class="company-label">Location</div>
            <span
              class="company-value"
              v-html="
                formatLocationString(
                  companyData.street,
                  companyData.city,
                  companyData.state,
                  companyData.country
                )
              "
            ></span>
          </div>
        </div>
        <div
          class="phone d-flex float-l width-100"
          v-if="companyData.phoneNumber"
        >
          <Phone-Icon viewBox="0 0 20 20" />
          <div class="label-with-value">
            <div class="company-label">Company phone</div>
            <span class="company-value"
              ><span>{{ companyData.phoneNumber }}</span>
              <Copy :value="companyData.phoneNumber" :showBorder="false" />
            </span>
          </div>
        </div>
        <div
          class="company-count d-flex float-l width-100"
          v-if="companyData.employeeCount"
        >
          <Company-Count viewBox="0 0 20 20" />
          <div class="label-with-value">
            <div class="company-label">Number of employees</div>
            <span class="company-value">{{ companyData.employeeCount }}</span>
          </div>
        </div>
        <div
          class="revenue d-flex float-l width-100"
          v-if="companyData.revenue"
        >
          <Revenue viewBox="0 0 20 20" />
          <div class="label-with-value">
            <div class="company-label">Revenue</div>
            <span class="company-value">{{ companyData.revenue }}</span>
          </div>
        </div>
        <div
          class="social-link d-flex float-l width-100"
          v-if="
            showSocialLinks && companyData && companyData.linkedInCompanyPageUrl
          "
        >
          <Link-Icon viewBox="0 0 18 18" />
          <div class="label-with-value">
            <div class="company-label">Social media links</div>
            <a
              :href="
                checkAndAddHttpProtocol(companyData.linkedInCompanyPageUrl)
              "
              target="_blank"
              class="txt-blue ellipsis linkedin"
            >
              <Linkedin-Logo-Icon viewBox="0 0 382 382" />
              <span>Linkedin</span></a
            >
          </div>
        </div>
      </div>
    </div>

    <div class="view-contact-btn-holder" v-if="!loadingCompany">
      <div
        class="view-all"
        v-if="shouldShowViewAllContacts"
        @click="showAllOtherContact"
        :data-link="companyData.primaryDomain"
      >
        <span>View all contacts</span>
      </div>
    </div>

    <div v-if="loadingCompany" class="placeholder">
      <div class="company-info-section">
        <div class="company-info">
          <div class="company-logo-with-name">
            <div class="logo-wrap flex-c">
              <img src="" class="company-logo linear-background" />
            </div>
            <div class="name-wrap">
              <div class="company-name linear-background"></div>
              <p class="company-industry linear-background"></p>
            </div>
          </div>

          <div class="decision-link">
            <div class="icon linear-background"></div>
            <div class="label-with-value">
              <div class="label-name linear-background"></div>
              <div class="label-name linear-background"></div>
            </div>
          </div>
          <div class="company-count">
            <div class="icon linear-background"></div>
            <div class="label-with-value">
              <div class="label-name linear-background"></div>
              <div class="label-name linear-background"></div>
            </div>
          </div>
          <div class="revenue">
            <div class="icon linear-background"></div>
            <div class="label-with-value">
              <div class="label-name linear-background"></div>
              <div class="label-name linear-background"></div>
            </div>
          </div>
          <div class="location">
            <div class="icon linear-background"></div>
            <div class="label-with-value">
              <div class="label-name linear-background"></div>
              <div class="label-name linear-background"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="view-contact" v-if="loadingCompany">
      <div class="btn linear-background"></div>
    </div>
  </div>
</template>

<script>
import decisionLinkIcon from '@/assets/icons/svg/grid-world-1.svg'
import companyCountIcon from '@/assets/icons/svg/group-2.svg'
import revenueIcon from '@/assets/icons/svg/revenue.svg'
import phoneIcon from '@/assets/icons/svg/phone.svg'
import locationIcon from '@/assets/icons/svg/placeholder-3.svg'
import linkedinLogoIcon from '@/assets/icons/svg/linkedin.svg'
import LinkIcon from '@/assets/icons/svg/link.svg'
import sharedFunctions from '@/assets/util/functions.js'
import { MxEvent } from '@/assets/util/constants/analytics'
import {
  advanceSearchUrl,
  TRIGGERS_TRACK_TO_LB
} from '@/assets/util/constants/constants.js'
import Copy from '@/components/Copy.vue'
import { checkAndAddHttpProtocol } from '@/assets/util/commonUtil.js'

export default {
  name: 'CompanyInfo',
  props: ['loadingCompany', 'companyData', 'pageType', 'showSocialLinks', 'totalContacts', 'contactsData'],
  data: function() {
    return {
      showActivationPopup: false
    }
  },
  computed: {
    isAccountVerified: function() {
      return this.$store.state.header.emailStatus == 0
    },
    shouldShowViewAllContacts: function() {
      if (
        !this.companyData ||
        !this.companyData.primaryDomain ||
        !this.companyData.name
      ) {
        return false
      }
      if (this.totalContacts != null && this.totalContacts <= 0) {
        return false
      }
      if (Array.isArray(this.contactsData) && this.contactsData.length === 0) {
        return false
      }
      return true
    }
  },
  components: {
    'Decision-Link': decisionLinkIcon,
    'Company-Count': companyCountIcon,
    Revenue: revenueIcon,
    'Phone-Icon': phoneIcon,
    Location: locationIcon,
    'Linkedin-Logo-Icon': linkedinLogoIcon,
    'Link-Icon': LinkIcon,
    Copy
  },
  methods: {
    checkAndAddHttpProtocol,
    showActivationError() {
      this.showActivationPopup = true
    },
    formatLocationString(street, city, state, country) {
      let str = ''
      str = str + (street ? street + (city ? ', ' : '') : '')
      str = str + (city ? city + (state ? ', ' : '') : '')
      str = str + (state ? state + (country ? ', ' : '') : '')
      str = str + (country ? country + '' : '')
      return str
    },
    showAllOtherContact(e) {
      let domain =
        e && e.currentTarget ? e.currentTarget.getAttribute('data-link') : ''
      const dataMap = {
        pageType: this.pageType
      }
      sharedFunctions.track(MxEvent.viewAllLeads, dataMap, this.$store.state)
      if (!this.isAccountVerified) this.showActivationError()
      else if (domain != '')
        window.open(
          advanceSearchUrl(
            domain,
            this.$store.state.header.uiVersion,
            TRIGGERS_TRACK_TO_LB.SINGLE_VIEW_CONTACTS
          )
        )
    }
  }
}
</script>

<style scoped lang="scss">
.company-info-section {
  position: relative;

  .company-info {
    padding: 16px;
    display: flex;
    flex-direction: column;
    width: 100%;
    .company-logo-with-name {
      display: flex;
      align-items: center;
      gap: 20px;
      .logo-wrap {
        border-radius: 50%;
        background: #ffffff;
        border: 1px solid rgba(102, 112, 128, 0.1);
        width: 40px;
        height: 40px;
        padding: 6px;
        justify-content: center;
        .company-logo {
          max-width: 100%;
          max-height: 100%;
        }
      }
      .name-wrap {
        .company-industry {
          margin: 0;
          font-size: 14px;
          line-height: 20px;
          color: #667080;
        }
      }
    }
    .company-name {
      font-size: 16px;
      line-height: 24px;
      color: #495567;
    }
    .phone {
      .company-value {
        display: flex;
        align-items: center;
        gap: 5px;
      }
    }
    .decision-link,
    .company-count,
    .revenue,
    .phone,
    .location,
    .social-link {
      display: flex;
      margin-top: 15px;
      padding: 0 4px;
      overflow: hidden;
      a {
        display: block;
        font-size: 15px;
        line-height: 20px;
      }
      svg {
        margin-top: 3px;
        flex-shrink: 0;
        width: 16px;
        height: 16px;
      }
    }
    .social-link .linkedin {
      width: fit-content;
      font-family: 'Figtree-Medium';
      margin-top: 5px;
      display: flex;
      gap: 6px;
      background: #ecedf4;
      border-radius: 6px;
      font-size: 13px;
      line-height: 24px;
      padding: 1px 9px;
      border: 1px solid transparent;
      &:hover {
        border: 1px solid $primary-color;
      }
    }
  }

  .label-with-value {
    margin-left: 15px;
    overflow: hidden;
    .company-label {
      font-size: 14px;
      line-height: 20px;
      color: #667080;
      margin-bottom: 1px;
    }
    .company-value {
      font-size: 15px;
      line-height: 20px;
      color: #495567;
    }
  }
}
.placeholder {
  .linear-background {
    margin-top: 10px;
    min-height: 15px;
    border-radius: 3px;
  }
  .company-info-section {
    display: flex;
    flex-direction: column;

    .icon {
      width: 20px;
      height: 15px;
      margin-right: 15px;
      margin-bottom: 20px;
    }
    .label-with-value {
      margin: 0;
      display: flex;
      flex-direction: column;
    }
    .label-name {
      min-width: 200px;
    }
    .name-wrap {
      .company-industry {
        margin-top: 10px !important;
      }
      > * {
        min-width: 200px;
      }
    }
  }
}

.view-contact-btn-holder {
  padding: 0 16px;
  width: 100%;
  .view-all {
    font-size: 14px;
    line-height: 20px;
    color: $primary-color;
    padding: 7px 0;
    max-width: 100%;
    margin: 0 0 20px 0;
    width: 100%;
    background: #ffffff;
    border: 1px solid rgba(38, 72, 247, 0.2);
    border-radius: 6px;
  }
}
.view-contact {
  margin: 10px 25px;
}
</style>
